import { Exercise as ComponentExercise, ExerciseType, DragAndDropData } from "@/services/admin/exercises.interfaces";
import { EditorInterface } from "./EditorInterface";
import { MoveHorizontal } from "lucide-react";

// Adicionar propriedades estendidas para o tipo DragAndDropData
interface ExtendedDragAndDropData extends DragAndDropData {
  question?: string;
  items?: any[];
}

/**
 * Editor para exercícios de arrastar e soltar (versão unificada)
 * Combina as funcionalidades duplicadas das implementações anteriores
 */
export class DragAndDropInfo extends EditorInterface {
  /**
   * Retorna as informações para exibir no cartão do exercício
   * @param exercise Objeto do exercício
   */
  static GetCardInfo(exercise: ComponentExercise): {
    title: string;
    subtitle: string;
    description?: string;
  } {
    if (!exercise || (exercise.type !== ExerciseType.DragAndDrop && !DragAndDropInfo.SupportsExerciseType(exercise.type as unknown as string))) {
      return {
        title: 'Arrastar e Soltar',
        subtitle: 'Sem detalhes',
      };
    }

    try {
      // Tenta extrair os dados do exercício, lidando com diferentes formatos
      let question = 'Sem pergunta';
      let items: any[] = [];
      
      // Se os dados estiverem no formato aninhado (data é um objeto)
      if (exercise.data && typeof exercise.data === 'object') {
        const data = exercise.data as unknown as ExtendedDragAndDropData;
        question = data.question || 'Sem pergunta';
        items = data.items || [];
      } 
      // Se os dados estiverem no formato plano
      else if ('question' in exercise) {
        question = (exercise as any).question || 'Sem pergunta';
        items = (exercise as any).items || [];
      }
      
      // Conta o número de itens
      const itemsCount = items.length;
      
      return {
        title: "Arrastar e Soltar",
        subtitle: question.length > 60 ? `${question.substring(0, 60)}...` : question,
        description: itemsCount > 0 ? `${itemsCount} itens para organizar` : "Sem itens definidos",
      };
    } catch (error) {
      console.error("Erro ao processar dados do exercício de arrastar e soltar:", error);
      return {
        title: 'Arrastar e Soltar',
        subtitle: 'Erro ao processar dados',
      };
    }
  }
  
  /**
   * Retorna o título do tipo de exercício
   */
  static getTitle(): string {
    return "Arrastar e Soltar";
  }
  
  /**
   * Retorna uma descrição formatada para o exercício
   * @param exercise Objeto do exercício
   */
  static getDescription(exercise: ComponentExercise): string {
    if (!exercise) {
      return "Sem detalhes";
    }
    
    try {
      // Tenta extrair os itens do exercício, lidando com diferentes formatos
      let items: any[] = [];
      
      // Se os dados estiverem no formato aninhado (data é um objeto)
      if (exercise.data && typeof exercise.data === 'object') {
        const data = exercise.data as unknown as ExtendedDragAndDropData;
        items = data.items || [];
      } 
      // Se os dados estiverem no formato plano
      else if ('items' in exercise) {
        items = (exercise as any).items || [];
      }
      
      const itemsCount = items.length;
      return itemsCount > 0 ? `${itemsCount} itens para organizar` : "Sem itens definidos";
    } catch (error) {
      console.error("Erro ao processar dados do exercício:", error);
      return "Erro ao processar dados";
    }
  }
  
  /**
   * Retorna o ícone para o tipo de exercício
   */
  static getIcon() {
    return MoveHorizontal;
  }
  
  /**
   * Verifica se o exercício é do tipo Arrastar e Soltar
   */
  static SupportsExerciseType(type: string): boolean {
    return type === ExerciseType.DragAndDrop;
  }
}